import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ROUTE_KEYS } from 'legacyfrontend/constants';
import { SearcherForm } from 'frontend/features/searchers';
import { Header } from './header';
import { getHeroStackStyle, getHeroTemplate } from './hero';
import type { BrandingProps } from './mapSettingsToBrandingProps';

interface BrandingHeroStackProps {
  branding: BrandingProps;
  showHeader?: boolean;
  showHero?: boolean;
}

// Each searcher result route (see routes.js) wraps UnifiedLayout in its own
// component, so React Router remounts this component on navigation between
// them — local state can't survive that. The active tab is derived from the
// route on these three so a search submit doesn't bounce back to the default.
const ROUTE_TO_SEARCHER_ID: Record<string, string> = {
  [ROUTE_KEYS.BUSCAR_X_PAQUETES]: 'paquetes',
  [ROUTE_KEYS.BUSCAR_X_VUELOS]: 'vuelos',
  [ROUTE_KEYS.BUSCAR_X_HOTELES]: 'hoteles',
};

function getRouteSearcherId(pathname: string): string | undefined {
  return ROUTE_TO_SEARCHER_ID[pathname.replace(/\/$/, '')];
}

/**
 * Header + Hero as one visual stack, owning the only piece of state they share:
 * which searcher tab is selected. The hero stays presentational — it receives a
 * `renderSearchForm` callback and never imports a search form itself.
 */
const BrandingHeroStack: React.FC<BrandingHeroStackProps> = ({
  branding,
  showHeader = true,
  showHero = true,
}) => {
  const { Component: Hero, headerVariant: variant } = getHeroTemplate(branding.templateCode);
  const { pathname } = useLocation();
  // The menu drawer portals into this node (instead of document.body) so it
  // stays confined to the header+hero block, reaching down to the end of the
  // hero, with its own internal scroll — mirrors PreviewRoute's setup.
  const [stackNode, setStackNode] = useState<HTMLDivElement | null>(null);
  const publishedSearcherId = branding.heroConfig.activeSearcherTabId || '';
  const routeSearcherId = getRouteSearcherId(pathname);
  const [activeSearcherId, setActiveSearcherId] = useState(routeSearcherId || publishedSearcherId);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  // Re-published settings (or a tenant change) reset the selection to whatever
  // Personalizar marked as the default tab, unless the current route is one of
  // the searcher result pages, which takes precedence.
  useEffect(() => {
    setActiveSearcherId(routeSearcherId || publishedSearcherId);
  }, [routeSearcherId, publishedSearcherId]);

  useEffect(() => {
    setMobileSearchOpen(false);
  }, [branding.templateCode]);

  const isFullBleed = variant === 'hero_a' && branding.headerConfig.fullBleed === true;
  const stackStyle = getHeroStackStyle(branding.heroConfig, isFullBleed, {
    reserveButtonColor: branding.headerConfig.brandColor2 || '#152956',
    iconColor: branding.headerConfig.iconColor || '#225e8f',
  });
  // Inline search only when a dedicated responsive image is set (prototipo v18).
  const hasMobileBg = Boolean((branding.heroConfig.mobileBackgroundImageUrl || '').trim());
  const mobileSearchInline = variant === 'hero_a' && hasMobileBg;

  const stackClassName = [
    'preview-hero-stack',
    `preview-hero-stack--${branding.templateCode}`,
    isFullBleed && 'preview-hero-stack--full-bleed',
    (variant === 'hero_a' || variant === 'reserve' || variant === 'ecommerce_slider') &&
      mobileSearchOpen &&
      'mobile-search-open',
    variant === 'hero_a' && mobileSearchInline && 'mobile-search-inline',
  ]
    .filter(Boolean)
    .join(' ');

  const contactStrip =
    variant === 'reserve'
      ? {
          contactColor: branding.headerConfig.contactColor,
          phone: branding.headerConfig.phone,
          showPhone: branding.headerConfig.showPhone,
          email: branding.headerConfig.email,
          showEmail: branding.headerConfig.showEmail,
          socialLinks: branding.headerConfig.socialLinks,
        }
      : undefined;

  return (
    <div className={stackClassName} ref={setStackNode} style={stackStyle}>
      {showHeader && (
        <Header
          config={{ ...branding.headerConfig, activeSearcherId }}
          heroSearchConfig={{ ...branding.heroConfig, activeSearcherTabId: activeSearcherId }}
          menuPortalContainer={stackNode}
          mobileSearchOpen={mobileSearchOpen}
          onMobileSearchToggle={() => setMobileSearchOpen((open) => !open)}
          onSearcherSelect={setActiveSearcherId}
          paisId={branding.footerOfficeInfo?.paisId}
          renderSearchForm={(searcherId) => <SearcherForm searcherId={searcherId} />}
          variant={variant}
        />
      )}
      {showHero && (
        <Hero
          config={{ ...branding.heroConfig, activeSearcherTabId: activeSearcherId }}
          contactStrip={contactStrip}
          mobileSearchOpen={mobileSearchOpen}
          onSearcherSelect={setActiveSearcherId}
          paisId={branding.footerOfficeInfo?.paisId}
          renderSearchForm={(searcherId) => <SearcherForm searcherId={searcherId} />}
        />
      )}
    </div>
  );
};

export default BrandingHeroStack;
